import React, { useEffect } from 'react';
import './PrivacyPage.css';

const EFFECTIVE_DATE = 'February 8, 2026';
const LAST_UPDATED = 'February 8, 2026';

const sections = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'information-we-collect', label: 'Information We Collect' },
  { id: 'how-we-use', label: 'How We Use Your Information' },
  { id: 'sharing', label: 'Information Sharing & Third Parties' },
  { id: 'storage-security', label: 'Data Storage & Security' },
  { id: 'cookies', label: 'Cookies & Local Storage' },
  { id: 'your-rights', label: 'Your Rights' },
  { id: 'data-retention', label: 'Data Retention' },
  { id: 'account-deletion', label: 'Account Deletion' },
  { id: 'children', label: "Children's Privacy" },
  { id: 'international-transfers', label: 'International Data Transfers' },
  { id: 'changes', label: 'Changes to This Policy' },
  { id: 'contact', label: 'Contact Information' },
];

const PrivacyPage = () => {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="page-container legal-page privacy-page">
      <h1>Privacy Policy</h1>
      <p className="privacy-effective-date">
        Effective Date: {EFFECTIVE_DATE} &nbsp;|&nbsp; Last Updated: {LAST_UPDATED}
      </p>

      <p>
        Welcome to <strong>The Orbital Ledger</strong> ("TOL," "we," "us," or "our"), a community
        companion web application for <em>Star Citizen</em> available at{' '}
        <a href="https://theorbitalledger.com" target="_blank" rel="noopener noreferrer">
          theorbitalledger.com
        </a>. This Privacy Policy explains how we collect, use, disclose, and
        safeguard your information when you use our Service, including our website, tools,
        and any associated features (collectively, the "Service").
      </p>

      <p>
        By accessing or using the Service, you acknowledge that you have read, understood,
        and agree to be bound by this Privacy Policy. If you do not agree with the terms of
        this Privacy Policy, please do not access or use the Service.
      </p>

      <div className="privacy-callout">
        <strong>Disclaimer:</strong> The Orbital Ledger is an independent community project and
        is <strong>not affiliated with, endorsed by, or sponsored by Cloud Imperium Games
        Corporation</strong> or any of its subsidiaries. <em>Star Citizen</em> is a registered
        trademark of Cloud Imperium Games Corporation.
      </div>

      {/* ── Table of Contents ── */}
      <nav className="privacy-toc">
        <p className="privacy-toc-title">Table of Contents</p>
        <ol>
          {sections.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`}>{s.label}</a>
            </li>
          ))}
        </ol>
      </nav>

      {/* ═══════════════════════════════════════════════════════
          1. INTRODUCTION
      ═══════════════════════════════════════════════════════ */}
      <h2 id="introduction">1. Introduction</h2>
      <p>
        The Orbital Ledger is a Software-as-a-Service (SaaS) platform that provides Star Citizen
        players with inventory tracking, trade route planning, commodity price charts,
        organization management tools, and a player marketplace. We offer both a free tier
        and paid subscription plans.
      </p>
      <p>
        We are committed to protecting your privacy. This policy describes the types of
        information we may collect from you or that you may provide when you use The Orbital
        Ledger, and our practices for collecting, using, maintaining, protecting, and
        disclosing that information.
      </p>

      {/* ═══════════════════════════════════════════════════════
          2. INFORMATION WE COLLECT
      ═══════════════════════════════════════════════════════ */}
      <h2 id="information-we-collect">2. Information We Collect</h2>
      <p>
        We collect several categories of information depending on how you interact with
        the Service. Below is a detailed breakdown.
      </p>

      {/* 2.1 Account Information */}
      <h3>2.1 Account Information</h3>
      <p>When you create an account, we collect:</p>
      <ul>
        <li><strong>Email address</strong> — used for authentication, account recovery, and essential service communications</li>
        <li><strong>Username</strong> — your chosen display name on The Orbital Ledger</li>
        <li><strong>Star Citizen handle</strong> — your in-game player name, used to link your TOL profile to your Star Citizen identity</li>
        <li><strong>Password</strong> — stored only as a one-way bcrypt hash; we never store or have access to your plaintext password</li>
      </ul>

      {/* 2.2 Profile Information */}
      <h3>2.2 Profile Information</h3>
      <p>You may optionally provide:</p>
      <ul>
        <li><strong>Profile avatar</strong> — an image you upload, stored securely in our file storage system</li>
        <li><strong>Display preferences</strong> — your UI settings such as theme, layout, and notification preferences</li>
        <li><strong>Badge selections</strong> — badges you choose to display on your public profile</li>
      </ul>

      {/* 2.3 Game Data */}
      <h3>2.3 Game &amp; Activity Data</h3>
      <p>
        To provide our core service features, we store game-related data that you enter
        or generate while using the platform:
      </p>
      <ul>
        <li><strong>Inventory items</strong> — weapons, armor, components, and other tracked items</li>
        <li><strong>Ship hangar</strong> — your fleet of ships and vehicles</li>
        <li><strong>Wallet balances</strong> — your in-game currency tracking (aUEC)</li>
        <li><strong>Trade routes</strong> — routes you create or save for trade planning</li>
        <li><strong>Price submissions</strong> — commodity prices you submit to help the community</li>
        <li><strong>Loadouts</strong> — ship and character loadout configurations</li>
        <li><strong>Wishlist</strong> — items you have bookmarked for future reference</li>
      </ul>

      {/* 2.4 Organization Data */}
      <h3>2.4 Organization Data</h3>
      <p>
        If you join or create an organization on The Orbital Ledger, we store:
      </p>
      <ul>
        <li><strong>Membership &amp; roles</strong> — your membership status and assigned role within the organization</li>
        <li><strong>Organization cargo</strong> — shared cargo inventories managed at the org level</li>
        <li><strong>Organization fleet</strong> — ships contributed or assigned to the org</li>
        <li><strong>Organization wallet</strong> — shared financial records and transactions</li>
        <li><strong>DKP (Dragon Kill Points)</strong> — participation and reward points tracked for org events</li>
        <li><strong>Events &amp; operations</strong> — org event details, participant lists, and operational data</li>
      </ul>

      {/* 2.5 Marketplace Data */}
      <h3>2.5 Marketplace Data</h3>
      <p>
        If you use our player marketplace features, we collect and store:
      </p>
      <ul>
        <li><strong>Listings</strong> — items or services you post for sale or trade</li>
        <li><strong>Transactions</strong> — records of completed marketplace exchanges</li>
        <li><strong>Reviews</strong> — ratings and feedback you leave for other users</li>
        <li><strong>Messages</strong> — communications sent through our marketplace messaging system</li>
      </ul>

      {/* 2.6 Payment Data */}
      <h3>2.6 Payment Data</h3>
      <p>
        Payment processing for our paid subscription tiers is handled entirely by{' '}
        <strong>Stripe, Inc.</strong> We do <strong>not</strong> collect, process, or store
        your credit card numbers, bank account details, or other sensitive financial
        information on our servers. What we do store:
      </p>
      <ul>
        <li><strong>Stripe Customer ID</strong> — a reference identifier linking your TOL account to your Stripe customer record</li>
        <li><strong>Subscription status</strong> — whether your subscription is active, cancelled, past due, etc.</li>
        <li><strong>Plan tier</strong> — which subscription plan you are on</li>
      </ul>
      <p>
        For information about how Stripe handles your payment data, please review{' '}
        <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
          Stripe's Privacy Policy
        </a>.
      </p>

      {/* 2.7 Security Data */}
      <h3>2.7 Security &amp; Authentication Data</h3>
      <p>
        To protect your account and our platform, we collect and process:
      </p>
      <ul>
        <li><strong>Login attempts</strong> — timestamps and success/failure status of authentication attempts</li>
        <li><strong>Session tokens</strong> — stored in hashed form to maintain your authenticated sessions</li>
        <li><strong>Multi-factor authentication (MFA) secrets</strong> — stored in encrypted form if you enable MFA</li>
        <li><strong>Security events</strong> — records of security-relevant actions such as password changes, MFA enrollment, and suspicious activity</li>
      </ul>

      {/* 2.8 Analytics */}
      <h3>2.8 Usage &amp; Analytics Data</h3>
      <p>
        We collect limited analytics data to improve the Service:
      </p>
      <ul>
        <li><strong>Page views</strong> — which pages and features you visit</li>
        <li><strong>Referral source</strong> — how you arrived at our site (e.g., from a search engine or social media link)</li>
        <li><strong>Feature usage</strong> — how you interact with specific tools and features</li>
      </ul>
      <div className="privacy-callout">
        We do <strong>not</strong> currently use any third-party analytics services (e.g., Google
        Analytics). All usage data is collected and processed internally on our own
        infrastructure.
      </div>

      {/* ═══════════════════════════════════════════════════════
          3. HOW WE USE YOUR INFORMATION
      ═══════════════════════════════════════════════════════ */}
      <h2 id="how-we-use">3. How We Use Your Information</h2>
      <p>
        We use the information we collect for the following purposes:
      </p>
      <ul>
        <li><strong>Provide and maintain the Service</strong> — to operate core features including inventory tracking, trade route planning, price charts, org management, and the marketplace</li>
        <li><strong>Account management</strong> — to create and manage your account, authenticate your identity, and maintain your sessions</li>
        <li><strong>Process subscriptions</strong> — to manage your paid subscription status and coordinate with Stripe for payment processing</li>
        <li><strong>Personalization</strong> — to remember your preferences, display settings, and customizations</li>
        <li><strong>Community features</strong> — to enable organization management, marketplace interactions, and social features</li>
        <li><strong>Security</strong> — to detect and prevent fraud, abuse, unauthorized access, and other harmful activities</li>
        <li><strong>Moderation</strong> — to enforce our <a href="/community-rules">Community Rules</a> and <a href="/terms">Terms of Service</a></li>
        <li><strong>Improvement</strong> — to analyze usage patterns and improve the Service's features, performance, and user experience</li>
        <li><strong>Communication</strong> — to send you essential service-related notifications (e.g., password resets, security alerts, subscription changes)</li>
        <li><strong>Legal compliance</strong> — to comply with applicable laws, regulations, and legal processes</li>
      </ul>

      {/* ═══════════════════════════════════════════════════════
          4. INFORMATION SHARING & THIRD PARTIES
      ═══════════════════════════════════════════════════════ */}
      <h2 id="sharing">4. Information Sharing &amp; Third Parties</h2>

      <div className="privacy-callout">
        <strong>We do not sell, rent, or trade your personal information to third parties.</strong> We
        will never monetize your data by selling it to advertisers, data brokers, or any
        other entity.
      </div>

      <p>
        We share your information only in the following limited circumstances:
      </p>

      <h3>4.1 Service Providers</h3>
      <p>
        We use the following third-party service providers to operate the Service. These
        providers access your data only as necessary to perform their functions and are
        contractually obligated to protect it:
      </p>

      <div className="privacy-table-wrap">
        <table className="privacy-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Purpose</th>
              <th>Data Accessed</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Supabase</strong></td>
              <td>Database hosting (PostgreSQL), user authentication, file storage</td>
              <td>All account data, game data, profile avatars, authentication tokens</td>
            </tr>
            <tr>
              <td><strong>Stripe</strong></td>
              <td>Payment processing for subscriptions</td>
              <td>Payment information you provide directly to Stripe; we receive only customer ID and subscription status</td>
            </tr>
            <tr>
              <td><strong>Vercel</strong></td>
              <td>Website hosting, CDN, and edge delivery</td>
              <td>Standard HTTP request data (IP address, user agent, request URL) for content delivery and performance</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>4.2 Other Users</h3>
      <p>
        Certain information you provide may be visible to other users of the Service,
        depending on your settings and actions:
      </p>
      <ul>
        <li>Your username and Star Citizen handle may be visible on your public profile</li>
        <li>Your profile avatar and selected badges are publicly displayed</li>
        <li>Marketplace listings, reviews, and transaction history are visible to other marketplace users</li>
        <li>Organization data may be visible to other members of your organization based on role permissions</li>
        <li>Price submissions contribute to community-wide price data and may be associated with your username</li>
      </ul>

      <h3>4.3 Legal Requirements</h3>
      <p>
        We may disclose your information if required to do so by law or in response to
        valid requests by public authorities (e.g., a court order, subpoena, or government
        agency request). We may also disclose information when we believe in good faith that
        disclosure is necessary to:
      </p>
      <ul>
        <li>Comply with a legal obligation</li>
        <li>Protect and defend our rights or property</li>
        <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
        <li>Protect the personal safety of users of the Service or the public</li>
      </ul>

      <h3>4.4 Business Transfers</h3>
      <p>
        In the event of a merger, acquisition, reorganization, bankruptcy, or sale of all
        or a portion of our assets, your information may be transferred as part of that
        transaction. We will notify you via email and/or a prominent notice on the Service
        before your information becomes subject to a different privacy policy.
      </p>

      {/* ═══════════════════════════════════════════════════════
          5. DATA STORAGE & SECURITY
      ═══════════════════════════════════════════════════════ */}
      <h2 id="storage-security">5. Data Storage &amp; Security</h2>
      <p>
        We take the security of your data seriously and employ multiple layers of
        protection:
      </p>

      <h3>5.1 Encryption</h3>
      <ul>
        <li><strong>In transit</strong> — All data transmitted between your browser and our servers is encrypted using HTTPS (TLS 1.2 or higher)</li>
        <li><strong>At rest</strong> — Data stored in our database is encrypted at rest using industry-standard encryption provided by our infrastructure providers</li>
        <li><strong>Passwords</strong> — All passwords are hashed using bcrypt with appropriate cost factors; we never store plaintext passwords</li>
        <li><strong>MFA secrets</strong> — Multi-factor authentication secrets are encrypted using strong symmetric encryption before storage</li>
        <li><strong>Session tokens</strong> — All session tokens are stored in hashed form</li>
      </ul>

      <h3>5.2 Infrastructure Security</h3>
      <ul>
        <li>Our database is hosted on Supabase's managed PostgreSQL infrastructure with automated backups, access controls, and network isolation</li>
        <li>Our web application is served through Vercel's global CDN with DDoS protection and edge security</li>
        <li>Administrative access to production systems is restricted and audited</li>
      </ul>

      <h3>5.3 Application Security</h3>
      <ul>
        <li>Row-level security (RLS) policies in our database ensure users can only access their own data</li>
        <li>Rate limiting is applied to authentication endpoints to prevent brute-force attacks</li>
        <li>Security events (login attempts, password changes, suspicious activity) are logged and monitored</li>
        <li>Input validation and parameterized queries protect against injection attacks</li>
      </ul>

      <div className="privacy-callout">
        While we implement robust security measures, no method of transmission over the
        Internet or method of electronic storage is 100% secure. We cannot guarantee
        absolute security of your data, but we commit to promptly notifying affected users
        in the event of a data breach in accordance with applicable laws.
      </div>

      {/* ═══════════════════════════════════════════════════════
          6. COOKIES & LOCAL STORAGE
      ═══════════════════════════════════════════════════════ */}
      <h2 id="cookies">6. Cookies &amp; Local Storage</h2>
      <p>
        We use a minimal set of cookies and browser storage mechanisms to operate the
        Service:
      </p>

      <div className="privacy-table-wrap">
        <table className="privacy-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Name / Purpose</th>
              <th>Duration</th>
              <th>Required?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Cookie</td>
              <td><strong>Supabase auth session</strong> — Maintains your authenticated session so you stay logged in</td>
              <td>Session / up to 7 days</td>
              <td>Yes (essential)</td>
            </tr>
            <tr>
              <td>Local Storage</td>
              <td><strong>User preferences</strong> — Stores your display settings, theme, and UI customizations locally in your browser</td>
              <td>Persistent until cleared</td>
              <td>Optional</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        We do <strong>not</strong> use third-party tracking cookies, advertising cookies,
        or any cross-site tracking mechanisms. We do not participate in ad networks or
        share cookie data with third parties.
      </p>
      <p>
        You can control cookies through your browser settings. Disabling essential cookies
        may prevent you from logging in or using authenticated features of the Service.
      </p>

      {/* ═══════════════════════════════════════════════════════
          7. YOUR RIGHTS
      ═══════════════════════════════════════════════════════ */}
      <h2 id="your-rights">7. Your Rights</h2>
      <p>
        Depending on your jurisdiction, you may have certain rights regarding your personal
        data. We honor the following rights for all users regardless of location, to the
        extent practicable:
      </p>

      <h3>7.1 Rights Under the General Data Protection Regulation (GDPR)</h3>
      <p>
        If you are located in the European Economic Area (EEA), the United Kingdom, or
        Switzerland, you have the following rights under the GDPR:
      </p>
      <ul>
        <li><strong>Right of Access</strong> — You have the right to request a copy of the personal data we hold about you</li>
        <li><strong>Right to Rectification</strong> — You have the right to request correction of inaccurate or incomplete personal data</li>
        <li><strong>Right to Erasure ("Right to be Forgotten")</strong> — You have the right to request deletion of your personal data, subject to certain legal exceptions</li>
        <li><strong>Right to Data Portability</strong> — You have the right to receive your personal data in a structured, commonly used, machine-readable format and to transmit it to another controller</li>
        <li><strong>Right to Restrict Processing</strong> — You have the right to request that we limit the processing of your personal data under certain circumstances</li>
        <li><strong>Right to Object</strong> — You have the right to object to the processing of your personal data for certain purposes</li>
        <li><strong>Right to Withdraw Consent</strong> — Where processing is based on consent, you have the right to withdraw consent at any time without affecting the lawfulness of processing based on consent before its withdrawal</li>
      </ul>
      <p>
        Our legal basis for processing your personal data includes: performance of a
        contract (providing the Service you signed up for), legitimate interests (security,
        fraud prevention, service improvement), and consent (where explicitly given).
      </p>

      <h3>7.2 Rights Under the California Consumer Privacy Act (CCPA)</h3>
      <p>
        If you are a California resident, you have the following rights under the CCPA:
      </p>
      <ul>
        <li><strong>Right to Know</strong> — You have the right to request disclosure of the categories and specific pieces of personal information we have collected about you, the categories of sources, the business purpose for collecting it, and the categories of third parties with whom we share it</li>
        <li><strong>Right to Delete</strong> — You have the right to request deletion of your personal information, subject to certain exceptions</li>
        <li><strong>Right to Opt-Out of Sale</strong> — You have the right to opt out of the sale of your personal information. <em>Note: We do not sell your personal information.</em></li>
        <li><strong>Right to Non-Discrimination</strong> — We will not discriminate against you for exercising any of your CCPA rights. You will not receive a different level or quality of service for exercising your rights</li>
      </ul>

      <h3>7.3 Exercising Your Rights</h3>
      <p>
        To exercise any of the rights described above, you may:
      </p>
      <ul>
        <li>Use the self-service tools available in your account settings (e.g., editing your profile, exporting your data, deleting your account)</li>
        <li>Contact us at <a href="mailto:support@theorbitalledger.com">support@theorbitalledger.com</a> with your request</li>
      </ul>
      <p>
        We will respond to verified requests within 30 days (or sooner where required by
        applicable law). We may need to verify your identity before processing your request.
        If we need additional time, we will inform you of the reason and the extension period.
      </p>

      {/* ═══════════════════════════════════════════════════════
          8. DATA RETENTION
      ═══════════════════════════════════════════════════════ */}
      <h2 id="data-retention">8. Data Retention</h2>
      <p>
        We retain your data according to the following policies:
      </p>
      <ul>
        <li><strong>Active accounts</strong> — We retain all data associated with your account for as long as your account remains active and you continue to use the Service</li>
        <li><strong>Inactive accounts</strong> — Data for inactive accounts is retained indefinitely unless you request deletion</li>
        <li><strong>Deleted accounts</strong> — When you delete your account, we initiate a <strong>30-day grace period</strong> during which your data is marked for deletion but can still be recovered if you change your mind. After the 30-day grace period, all personal data is <strong>permanently and irreversibly deleted</strong> from our systems, including backups within a reasonable timeframe</li>
        <li><strong>Security logs</strong> — Security event logs may be retained for up to 90 days after account deletion for fraud prevention and legal compliance purposes</li>
        <li><strong>Anonymized data</strong> — Aggregated, anonymized data (such as aggregate price submission statistics) that cannot be used to identify you may be retained indefinitely for analytical purposes</li>
      </ul>

      {/* ═══════════════════════════════════════════════════════
          9. ACCOUNT DELETION
      ═══════════════════════════════════════════════════════ */}
      <h2 id="account-deletion">9. Account Deletion &amp; Data Removal</h2>
      <div className="privacy-deletion-block">
        <ol>
          <li>
            <strong>Account Deletion:</strong> Users may request to delete their account at any
            time through the application's "Danger Zone" settings. Account deletion requires
            password confirmation and will permanently remove all user data after the 30-day
            grace period.
          </li>
          <li>
            <strong>Ban Protection:</strong> For community safety and legal compliance, users who
            have been banned for violating our Terms of Service may be temporarily or permanently
            restricted from deleting their accounts. This restriction is necessary to:
            <ul className="privacy-sub-list">
              <li>Prevent immediate ban evasion</li>
              <li>Preserve evidence for legal proceedings if required</li>
              <li>Maintain the integrity of our moderation system</li>
            </ul>
          </li>
          <li>
            <strong>Data Minimization:</strong> Banned users may request anonymization of personal
            data while maintaining the account structure for moderation purposes. Upon such
            requests, we will minimize personal data in accordance with applicable privacy laws.
          </li>
          <li>
            <strong>Legal Requests:</strong> In all cases, we will comply with valid legal requests
            for data deletion or access as required by applicable laws, including GDPR, CCPA,
            and other privacy regulations.
          </li>
        </ol>
      </div>
      <p>
        To initiate account deletion, navigate to{' '}
        <strong>Settings &gt; Danger Zone &gt; Delete Account</strong> in the application.
        Alternatively, you may email{' '}
        <a href="mailto:support@theorbitalledger.com">support@theorbitalledger.com</a> to request
        account deletion.
      </p>

      {/* ═══════════════════════════════════════════════════════
          10. CHILDREN'S PRIVACY
      ═══════════════════════════════════════════════════════ */}
      <h2 id="children">10. Children's Privacy</h2>
      <p>
        The Orbital Ledger is not intended for use by children under the age of 13. We do
        not knowingly collect personal information from children under 13 years of age. If
        you are under 13, you may not create an account or use the Service.
      </p>
      <p>
        If we become aware that we have inadvertently collected personal information from a
        child under 13, we will take steps to delete that information as quickly as possible.
        If you believe that a child under 13 has provided us with personal information,
        please contact us at{' '}
        <a href="mailto:support@theorbitalledger.com">support@theorbitalledger.com</a>.
      </p>
      <p>
        For users between the ages of 13 and 18 (or the age of majority in your
        jurisdiction), we recommend that a parent or guardian review this Privacy Policy
        and supervise their use of the Service.
      </p>

      {/* ═══════════════════════════════════════════════════════
          11. INTERNATIONAL DATA TRANSFERS
      ═══════════════════════════════════════════════════════ */}
      <h2 id="international-transfers">11. International Data Transfers</h2>
      <p>
        The Orbital Ledger is operated from the United States. If you are accessing the
        Service from outside the United States, please be aware that your information may
        be transferred to, stored, and processed in the United States or other countries
        where our service providers maintain facilities.
      </p>
      <p>
        These countries may have data protection laws that are different from the laws of
        your country of residence. By using the Service, you consent to the transfer of
        your information to these countries.
      </p>
      <p>
        For users in the EEA, UK, or Switzerland: where we transfer personal data outside
        of these regions, we rely on appropriate safeguards such as Standard Contractual
        Clauses (SCCs) approved by the European Commission, or adequacy decisions, to
        ensure your data is protected in accordance with applicable data protection laws.
      </p>

      {/* ═══════════════════════════════════════════════════════
          12. CHANGES TO THIS POLICY
      ═══════════════════════════════════════════════════════ */}
      <h2 id="changes">12. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time to reflect changes in our
        practices, technology, legal requirements, or other factors. When we make changes:
      </p>
      <ul>
        <li>We will update the "Last Updated" date at the top of this page</li>
        <li>For material changes, we will provide notice through the Service (e.g., a banner notification) and/or send an email to the address associated with your account</li>
        <li>Your continued use of the Service after the effective date of any changes constitutes your acceptance of the updated Privacy Policy</li>
      </ul>
      <p>
        We encourage you to review this Privacy Policy periodically to stay informed about
        how we are protecting your information.
      </p>

      {/* ═══════════════════════════════════════════════════════
          13. CONTACT INFORMATION
      ═══════════════════════════════════════════════════════ */}
      <h2 id="contact">13. Contact Information</h2>
      <p>
        If you have any questions, concerns, or requests regarding this Privacy Policy or
        our data practices, please contact us:
      </p>

      <div className="privacy-contact">
        <p><strong>The Orbital Ledger — Privacy Inquiries</strong></p>
        <p>
          <a href="mailto:support@theorbitalledger.com">support@theorbitalledger.com</a>
        </p>
        <p style={{ fontSize: '0.88rem', color: '#7a8099', marginTop: 8 }}>
          We aim to respond to all privacy-related inquiries within 30 days.
        </p>
      </div>

      {/* ── Footer disclaimer ── */}
      <p className="privacy-disclaimer">
        The Orbital Ledger is an independent community project. <em>Star Citizen</em>,{' '}
        <em>Squadron 42</em>, and related marks are trademarks of Cloud Imperium Games
        Corporation. This website is not affiliated with or endorsed by Cloud Imperium
        Games Corporation.
      </p>
    </div>
  );
};

export default PrivacyPage;
